import React, { useEffect, useState, Children } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Check, ChevronDown, X } from 'lucide-react';
import { BlueprintGrid } from '../components/BlueprintGrid';
import { TopographicPattern } from '../components/TopographicPattern';
export function HowWeFixIt() {
  const location = useLocation();
  const [expandedStep, setExpandedStep] = useState<number | null>(null);
  // Auto-expand accordion and scroll when arriving via hash link
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (!hash) return;
    const hashToIndex: Record<string, number> = {
      'step-1': 0,
      'honest-conversation': 0,
      'step-2': 1,
      diagnostic: 1,
      'operating-diagnostic': 1,
      'constraint-diagnostic': 1,
      'step-3': 2,
      intervention: 2,
      'targeted-intervention': 2,
      'focused-redesign': 2
    };
    const idx = hashToIndex[hash];
    if (idx === undefined) return;
    setExpandedStep(idx);
    // Small delay so the accordion opens before we scroll
    setTimeout(() => {
      const el = document.getElementById(`step-${idx + 1}`);
      if (!el) return;
      const offset = 96;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({
        top,
        behavior: 'smooth'
      });
    }, 100);
  }, [location.hash]);
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const stagger = {
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const steps = [{
    num: '1',
    title: 'Honest Conversation',
    summary: 'A 45-minute working conversation to figure out whether this is a real business problem or just normal growth friction.',
    preview: 'Clarity + decision',
    duration: 'Free · 45 minutes',
    required: 'Required start',
    hasCta: true,
    sections: [{
      label: 'Purpose',
      marker: 'dot' as const,
      content: 'Separate deeper drag from normal growth friction and decide the next right step.'
    }, {
      label: 'What we do',
      marker: 'check' as const,
      list: ['Clarify what changed as the business grew', 'Clarify where the drag is actually coming from', 'Separate structural drag from noise or discomfort', 'Decide the next right step']
    }, {
      label: 'Possible outcomes',
      marker: 'arrow' as const,
      list: ["We're fine. Keep pushing.", "This is real, but it's not the right timing. Wait.", 'A Constraint Diagnostic is warranted.']
    }, {
      label: 'Done when',
      marker: 'dot' as const,
      content: 'We have a clean next step - including "keep pushing" if that\'s the truth. No pitch. No obligation.'
    }]
  }, {
    num: '2',
    title: 'Constraint Diagnostic',
    summary: 'A focused, time-boxed diagnostic to identify the primary constraint with evidence.',
    preview: 'Constraint + options',
    duration: 'Paid · 2-3 weeks',
    required: 'Step 2',
    hasCta: false,
    sections: [{
      label: 'Purpose',
      marker: 'dot' as const,
      content: 'Turn a fuzzy operating problem into a real decision about what is actually worth fixing.'
    }, {
      label: 'What we look at',
      marker: 'dot' as const,
      list: ['Lead-to-Revenue - how attention becomes qualified demand, committed deals, and booked money', 'Offer-to-Economics Loop - how learning becomes a tighter offer: clearer boundaries, smarter pricing/packaging, less complexity over time', "Fulfillment / Delivery - how what's sold gets produced, delivered, and made real", 'Service / Support - how issues get resolved, customers stay happy, and revenue expands instead of churns', 'Cash & Commitments - how money gets invoiced and collected, and obligations stay clean and current', 'Capacity & Resourcing - how time, people, inventory, and throughput get allocated so the business can keep its promises', "Systems & Truth - how information moves, definitions stay consistent, and leaders can trust what they're seeing"]
    }, {
      label: 'What you get',
      marker: 'check' as const,
      list: ['the primary constraint', 'the causes underneath it', 'options with tradeoffs', 'a recommended sequence', 'a do-not-do list']
    }, {
      label: 'What the sprint includes',
      marker: 'dot' as const,
      list: ['Kickoff (60 min)', 'Up to 6 interviews (45 min each)', 'Artifact review (you provide; volume is capped)', 'Synthesis working session (60 min)', 'Final readout (90 min)']
    }, {
      label: 'What this is not',
      marker: 'x' as const,
      list: ['A full-company audit', 'A tool shopping exercise', 'A list of 37 improvements', 'Implementation disguised as diagnosis', 'A guarantee of outcomes before reality is inspected']
    }, {
      label: 'Guardrails',
      marker: 'dot' as const,
      list: ['One client point person schedules everything', 'Interviews happen in week 1', 'Artifacts delivered within 48 hours', 'Meeting cap is fixed (anything else is out of scope)', 'Async Q&A limited to two short office-hour slots', 'The 10-day clock starts once scheduling + initial artifacts are received (late inputs pause the sprint clock)']
    }, {
      label: 'Done when',
      marker: 'dot' as const,
      content: 'We have the constraint diagnosis, options with tradeoffs, and a recommended sequence. The decision is yours.'
    }]
  }, {
    num: '3',
    title: 'Focused Redesign',
    summary: 'A scoped engagement to remove one meaningful constraint and make the change real in the business.',
    preview: 'Mechanism + handoff',
    duration: 'Paid · 4–6 weeks',
    required: 'Step 3',
    hasCta: false,
    sections: [{
      label: 'Purpose',
      marker: 'dot' as const,
      content: 'Redesign one important part of the business so work resolves lower, founder pull drops, and ownership gets clearer.'
    }, {
      label: 'What you get',
      marker: 'check' as const,
      list: ['One workflow running end-to-end in the real environment (thin slice → major cases)', 'Target operating design for that workflow (how it should run)', 'Decision rights + escalation rules (explicit and enforceable)', 'Ownership model (RACI-lite) for the workflow', "Guardrails + exception handling rules (so errors don't scale)", 'Signal definitions (what to watch weekly so it stays stable)', 'Handoff pack (runbook + Owner checklist) so the system runs without us']
    }, {
      label: 'What the sprint includes',
      marker: 'dot' as const,
      list: ['Kickoff (60 min) to lock scope, owner, and success criteria', 'Workflow redesign sessions (1–2) to define the target operating model + decision rules', 'Rapid prototype build inside existing tools (configuration, templates, lightweight automations)', 'Edge-case pressure test (60 min)', 'Enablement session (60 min) for the internal Owner + operators', 'Stabilization check (30–45 min) after the workflow runs in the wild', 'Final readout (60–90 min)']
    }, {
      label: 'Limited build',
      marker: 'dot' as const,
      content: "We build the minimum viable working system to prove the design - configuration, templates, and lightweight automations. Heavy build (custom dev, migrations, deep integrations) is spec'd and reviewed by us, executed by your team or vendors."
    }, {
      label: 'What this is not',
      marker: 'x' as const,
      list: ['Ongoing operational ownership', 'Indefinite meeting leadership', 'People management', 'Unlimited iteration until it feels perfect', 'A guarantee of business results without adoption']
    }, {
      label: 'Guardrails',
      marker: 'dot' as const,
      list: ['One primary constraint per sprint', 'One workflow end-to-end is the deliverable (anything else becomes another sprint)', 'One internal Owner is accountable for adoption (not us)', 'One client point person schedules and provides access', 'Meeting cap is fixed; async support limited to office-hour blocks']
    }, {
      label: 'Done when',
      marker: 'dot' as const,
      content: 'The constraint is no longer structurally true in the scoped workflow, decision logic and ownership are enforceable, and the workflow runs end-to-end in the tools with guardrails - without us being required.'
    }]
  }];
  return <main className="w-full" data-id="element-633">
      {/* Hero */}
      <section className="relative py-20 px-6 bg-paper pt-32 overflow-hidden" data-id="element-634">
        <div className="absolute inset-0 text-seaglass/[0.15]" data-id="element-635">
          <BlueprintGrid data-id="element-636" />
        </div>

        <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl mx-auto relative z-10" data-id="element-637">
          <motion.h1 variants={fadeIn} className="text-4xl md:text-6xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-638">
            We don&apos;t fix everything. We fix the next domino.
          </motion.h1>

          <motion.div variants={fadeIn} className="text-lg text-text max-w-2xl mb-8 space-y-4" data-id="element-639">
            <p data-id="element-640">
              When growth gets heavier, the instinct is predictable: add people,
              add tools, add meetings, add oversight. But fixing the wrong thing
              first usually makes the business heavier, slower, and more
              expensive to run.
            </p>
            <p className="font-medium text-navy" data-id="element-641">
              Our approach is simple: start with clarity, find the constraint,
              then decide whether anything is worth changing at all.
            </p>
          </motion.div>

          {/* Hero CTAs - Side by side on desktop, stacked on mobile */}
          <motion.div variants={fadeIn} className="flex flex-col sm:flex-row sm:items-start gap-6 mb-10" data-id="element-642">
            {/* Primary CTA with microcopy */}
            <div className="flex flex-col" data-id="element-643">
              <Link to="/honest-conversation" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300" data-id="element-644">
                <span data-id="element-645">Book an Honest Conversation</span>
                <ArrowRight className="w-5 h-5" data-id="element-646" />
              </Link>
              <motion.p variants={fadeIn} className="text-sm text-text-muted mt-2" data-id="element-647">
                45 minutes. No pitch. Just clarity.
              </motion.p>
            </div>

            {/* Secondary link with microcopy */}
            <div className="flex flex-col" data-id="element-648">
              <a href="#three-steps" className="inline-flex items-center space-x-2 px-6 py-4 border border-navy/20 hover:border-navy/40 text-navy/70 hover:text-navy font-medium transition-all duration-300" data-id="element-649">
                <span data-id="element-650">Jump to the three steps</span>
                <ChevronDown className="w-4 h-4" data-id="element-651" />
              </a>
              <motion.p variants={fadeIn} className="text-sm text-text-muted mt-2" data-id="element-652">
                See the full sequence below
              </motion.p>
            </div>
          </motion.div>
        </motion.div>

        {/* Scroll cue */}
        <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center" animate={{
        y: [0, 6, 0]
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-653">
          <ChevronDown className="w-5 h-5 text-seaglass/50" data-id="element-654" />
        </motion.div>
      </section>

      {/* Intro Section */}
      <section className="py-24 px-6 border-t hairline-top bg-paper-dark" data-id="element-655">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-656">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-657">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-658">
              Start small. Get clarity. Then decide what&apos;s worth touching.
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-659">
            <p className="text-text" data-id="element-660">This is not a transformation program.</p>
            <p className="text-text" data-id="element-661">
              It is not a long audit. It is not a giant backlog of improvements.
              It is not a new operating dependency.
            </p>
            <p className="text-text" data-id="element-662">
              It is a sequence built to answer one question:
            </p>

            <div className="border-l-2 border-seaglass pl-8 py-2" data-id="element-663">
              <p className="text-xs font-mono uppercase tracking-wider text-seaglass mb-2" data-id="element-664">
                The better move
              </p>
              <p className="text-body text-text font-medium mb-3" data-id="element-665">
                What is the constraint making the business harder to run than it
                should be?
              </p>
              <p className="text-body text-text" data-id="element-666">
                Once that is clear, we decide whether the right move is to leave
                it alone, diagnose it more deeply, or redesign one important
                part of the system.
              </p>
            </div>

            <p className="text-text font-medium text-navy" data-id="element-667">
              Technology follows clarity. We do not automate confusion.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* What this work is — and is not */}
      <section className="py-24 px-6 bg-paper" data-id="element-668">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-669">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-670">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-671">
              This is different from operating help, coaching, or generic
              consulting.
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-672">
            <p className="text-text" data-id="element-673">
              A good operator helps run the business better.
            </p>
            <p className="text-text" data-id="element-674">A coach helps the founder think better.</p>
            <p className="text-text" data-id="element-675">
              A consultant often brings advice, frameworks, or execution
              support.
            </p>
            <p className="text-navy font-medium" data-id="element-676">
              This work is different. We identify the structural constraint
              growth has exposed, redesign the right mechanism, and leave the
              business less dependent on heroic effort.
            </p>

            <p className="text-text mt-4" data-id="element-677">
              Most process is compensating for missing decision logic. That is
              why we start with the constraint, not the symptom.
            </p>

            <div className="border-l-2 border-seaglass pl-8 py-2 mt-4" data-id="element-678">
              <p className="text-body text-text font-medium" data-id="element-679">
                If the solution depends on us staying involved, we did not solve
                the right problem.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* What We Review — Constraint Diagnostic preview */}
      <section className="py-24 px-6 bg-paper" data-id="element-680">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-5xl mx-auto" data-id="element-681">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-682">
            <p className="eyebrow mb-4 text-seaglass" data-id="element-683">
              The Constraint Diagnostic
            </p>
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-684">
              We scan the whole business first, then zoom in on the constraint.
            </h2>
            <div className="space-y-4 max-w-3xl" data-id="element-685">
              <p className="text-body text-text" data-id="element-686">
                We take a fast pass across the few flows that make the business
                work end to end.
              </p>
              <p className="text-body text-text" data-id="element-687">
                Then we go deep where the drag is actually being created.
              </p>
              <p className="text-body text-text" data-id="element-688">
                That matters because the loudest symptom is not always the
                source.
              </p>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-10" data-id="element-689">
            {[{
            num: '01',
            name: 'Lead-to-Revenue',
            desc: 'How attention becomes qualified demand, committed deals, and booked money.',
            highlight: true
          }, {
            num: '02',
            name: 'Offer-to-Economics Loop',
            desc: 'How learning becomes a tighter offer: clearer boundaries, smarter pricing/packaging, less complexity over time.'
          }, {
            num: '03',
            name: 'Fulfillment / Delivery',
            desc: "How what's sold gets produced, delivered, and made real."
          }, {
            num: '04',
            name: 'Service / Support',
            desc: 'How issues get resolved, customers stay happy, and revenue expands instead of churns.'
          }, {
            num: '05',
            name: 'Cash & Commitments',
            desc: 'How money gets invoiced and collected, and obligations stay clean and current.'
          }, {
            num: '06',
            name: 'Capacity & Resourcing',
            desc: 'How time, people, inventory, and throughput get allocated so the business can keep its promises.'
          }, {
            num: '07',
            name: 'Systems & Truth',
            desc: "How information moves, definitions stay consistent, and leaders can trust what they're seeing."
          }].map(flow => <motion.div key={flow.num} variants={fadeIn} className={['p-6 bg-white border', flow.highlight ? 'border-l-[3px] border-l-seaglass border-t-seaglass/15 border-r-seaglass/15 border-b-seaglass/15 bg-seaglass/[0.03]' : 'border-seaglass/10'].join(' ')} data-id="element-690">
                <p className="text-xs font-mono font-semibold text-seaglass uppercase tracking-wider mb-3" data-id="element-691">
                  {flow.num}
                </p>
                <h3 className="text-lg font-semibold text-navy mb-2 leading-tight" data-id="element-692">
                  {flow.name}
                </h3>
                <p className="text-sm text-text leading-relaxed" data-id="element-693">{flow.desc}</p>
              </motion.div>)}
          </div>

          <motion.div variants={fadeIn} data-id="element-694">
            <div className="border-l-2 border-seaglass pl-8 py-2 mb-10" data-id="element-695">
              <p className="text-body text-text font-medium mb-2" data-id="element-696">
                This is the fastest way to find the real constraint without
                turning the business into a long audit.
              </p>
              <p className="text-body text-text" data-id="element-697">
                Interviews are capped. Artifacts are scoped. The sprint is
                time-boxed. Your team gives us focused time, and we give you a
                decision. Not a backlog.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4" data-id="element-698">
              <a href="#three-steps" className="inline-flex items-center space-x-2 text-seaglass hover:text-seaglass-light font-medium transition-colors duration-300" data-id="element-699">
                <span data-id="element-700">See the full 3-Step Sequence</span>
                <ArrowRight className="w-4 h-4" data-id="element-701" />
              </a>
              <Link to="/whats-wrong#six-taxes" className="inline-flex items-center space-x-2 text-seaglass hover:text-seaglass-light font-medium transition-colors duration-300" data-id="element-702">
                <span data-id="element-703">Read about the Six Structural Taxes</span>
                <ArrowRight className="w-4 h-4" data-id="element-704" />
              </Link>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Three Steps */}
      <section id="three-steps" className="relative py-24 px-6 bg-[#8BA89F26] grain border-y border-seaglass/20 overflow-hidden" data-id="element-705">
        <TopographicPattern color="sage" opacity={0.06} data-id="element-706" />

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto relative z-10" data-id="element-707">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-708">
            <h2 className="text-5xl md:text-6xl font-serif font-semibold text-navy mb-4 leading-tight" data-id="element-709">
              Three steps in order
            </h2>

            <p className="text-lg text-text mb-3 max-w-3xl" data-id="element-710">
              Each step has a clear output and a stopping point. We only move to
              the next step when it&apos;s worth the time and money.
            </p>

            <p className="text-sm text-text-muted" data-id="element-711">
              Everyone starts with Step 1. Steps 2 and 3 are only recommended if
              the drag is structural and worth touching.
            </p>
          </motion.div>

          <div className="space-y-0" data-id="element-712">
            {steps.map((step, i) => <motion.div key={i} id={`step-${step.num}`} variants={fadeIn} className="border-b border-seaglass/20 last:border-0" data-id="element-713">
                <button onClick={() => setExpandedStep(expandedStep === i ? null : i)} className="w-full py-6 flex items-start justify-between text-left group" data-id="element-714">
                  <div className="flex-1 pr-6" data-id="element-715">
                    <div className="flex items-start justify-between mb-2" data-id="element-716">
                      <h3 className="text-2xl font-serif font-semibold text-navy" data-id="element-717">
                        {step.num}) {step.title}
                      </h3>
                      <div className="text-right ml-4 flex-shrink-0" data-id="element-718">
                        <div className="text-xs font-medium text-seaglass uppercase tracking-wider" data-id="element-719">
                          {step.required}
                        </div>
                        <div className="text-xs text-text-muted" data-id="element-720">
                          {step.duration}
                        </div>
                      </div>
                    </div>
                    <p className="text-base text-text mb-1" data-id="element-721">{step.summary}</p>
                    <p className="text-xs font-mono text-seaglass uppercase tracking-wider" data-id="element-722">
                      {step.preview}
                    </p>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-seaglass flex-shrink-0 transition-transform duration-300 mt-1 ${expandedStep === i ? 'rotate-180' : ''}`} data-id="element-723" />
                </button>

                {expandedStep === i && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3
            }} className="pb-8" data-id="element-724">
                    <div className="space-y-6" data-id="element-725">
                      {step.sections.map((section, j) => <div key={j} className="py-4 border-t border-seaglass/20 first:border-0" data-id="element-726">
                          <p className="text-base font-semibold text-navy mb-3" data-id="element-727">
                            {section.label}
                          </p>
                          {'content' in section && section.content && <p className="text-base text-text leading-relaxed" data-id="element-728">
                              {section.content}
                            </p>}
                          {'list' in section && section.list && <ul className="space-y-2" data-id="element-729">
                              {section.list.map((item, k) => <li key={k} className="flex items-start space-x-2 text-base text-text" data-id="element-730">
                                  {section.marker === 'check' ? <Check className="w-5 h-5 text-seaglass flex-shrink-0 mt-0.5" data-id="element-731" /> : section.marker === 'x' ? <X className="w-5 h-5 text-seaglass flex-shrink-0 mt-0.5" data-id="element-732" /> : section.marker === 'arrow' ? <ArrowRight className="w-3.5 h-3.5 text-seaglass flex-shrink-0 mt-1.5" data-id="element-733" /> : <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-734" />}
                                  <span data-id="element-735">{item}</span>
                                </li>)}
                            </ul>}
                        </div>)}

                      {step.hasCta && <div className="pt-4 border-t border-seaglass/20" data-id="element-736">
                          <Link to="/honest-conversation" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300" data-id="element-737">
                            <span data-id="element-738">Book an Honest Conversation</span>
                            <ArrowRight className="w-5 h-5" data-id="element-739" />
                          </Link>
                        </div>}
                    </div>
                  </motion.div>}
              </motion.div>)}
          </div>
        </motion.div>
      </section>

      {/* Boundaries and principles */}
      <section className="py-24 px-6 bg-paper" data-id="element-740">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-741">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-742">
            <h2 className="text-3xl md:text-4xl font-serif font-semibold text-navy mb-6" data-id="element-743">
              One constraint at a time. Clean exits. No dependency.
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6 text-body" data-id="element-744">
            <p className="text-text" data-id="element-745">
              A few things are true about how we work:
            </p>
            <ul className="space-y-3" data-id="element-746">
              {['We do not try to fix the whole company at once', 'We do not create a new layer of process just to feel organized', 'We do not use software as a substitute for clear rules', 'We use existing tools where possible', 'We make the smallest change that removes the most load', 'Fix the recurring failure, not every edge case', 'We aim for independence, not dependence'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-747">
                  <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-748" />
                  <span className="text-text" data-id="element-749">{item}</span>
                </li>)}
            </ul>
            <p className="text-text font-medium text-navy" data-id="element-750">
              If the fix still requires us to sit in the middle, it is not done.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* Dark CTA Band */}
      <section className="relative py-24 px-6 bg-navy grain overflow-hidden" data-id="element-751">
        <div className="absolute inset-0 text-white/[0.05]" data-id="element-752">
          <BlueprintGrid data-id="element-753" />
        </div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="relative max-w-3xl mx-auto text-center z-10" data-id="element-754">
          <h2 className="text-4xl md:text-5xl font-serif font-semibold text-white mb-6 leading-tight" data-id="element-755">
            Clarity first. Then the right move.
          </h2>
          <p className="text-lg text-white/80 mb-10" data-id="element-756">
            If there is a deeper issue, we can make it visible. If there is not,
            we will tell you that too.
          </p>
          <Link to="/honest-conversation" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300" data-id="element-757">
            <span data-id="element-758">Book an Honest Conversation</span>
            <ArrowRight className="w-5 h-5" data-id="element-759" />
          </Link>
        </motion.div>
      </section>
    </main>;
}